import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { packingSlipService } from '../../services/api';
import { toast } from 'react-toastify';
import logoSrc from '../../assets/img/fit2ride-bl-logo.png';
import PrintablePackingSlip from './PrintablePackingSlip';

const PackingSlipDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [packingSlip, setPackingSlip] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        loadPackingSlip();
    }, [id]);

    useEffect(() => {
        // Listen for when print dialog is closed
        const handleAfterPrint = async () => {
            // If status is pending, update to packed after printing
            if (packingSlip && packingSlip.status === 'pending') {
                try {
                    const response = await packingSlipService.updatePackingSlip(id, { status: 'packed' });
                    setPackingSlip(response.data);
                    toast.success('Packing slip marked as packed');
                } catch (error) {
                    console.error('Error updating packing slip status:', error);
                    toast.error('Failed to update status');
                }
            }
        };

        window.addEventListener('afterprint', handleAfterPrint);

        return () => {
            window.removeEventListener('afterprint', handleAfterPrint);
        };
    }, [id, packingSlip]);

    const loadPackingSlip = async () => {
        try {
            setLoading(true);
            const response = await packingSlipService.getPackingSlip(id);
            setPackingSlip(response.data);
        } catch (error) {
            console.error('Error fetching packing slip:', error);
            toast.error('Failed to load packing slip');
        } finally {
            setLoading(false);
        }
    };

    const handlePrint = () => {
        window.print();
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-screen">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
            </div>
        );
    }

    if (!packingSlip) {
        return (
            <div className="container mx-auto px-4 py-6">
                <div className="text-center py-8 text-gray-500">
                    Packing slip not found
                </div>
            </div>
        );
    }

    return (
        <>
            <style>{`
                /* Screen styles */
                .print-table {
                    width: 100%;
                    border-collapse: collapse;
                }

                .print-table thead,
                .print-table tfoot {
                    display: none;
                }

                .print-table tbody {
                    display: table-row-group;
                }

                .print-table tbody tr {
                    display: table-row;
                }

                .print-table tbody td {
                    display: table-cell;
                    border: none;
                }

                .print-header,
                .print-footer {
                    display: none;
                }

                .header-space,
                .footer-space {
                    display: none;
                }

                .print-only {
                    display: none;
                }

                .screen-only {
                    display: block;
                }

                @media print {
                
                    main {
                        padding: 0 !important;
                        margin: 0 !important;
                    }
                    .main-layout-bg {
                        background: #fff !important;
                    }
                    
                    /* Hide all navigation, headers, and non-printable elements */
                    .no-print,
                    header,
                    nav,
                    aside,
                    [class*="sidebar"],
                    [class*="Sidebar"],
                    [class*="Header"],
                    [class*="navigation"],
                    [role="navigation"],
                    button:not(.print-only button),
                    .Toastify {
                        display: none !important;
                    }

                    /* Page setup */
                    @page {
                        size: letter;
                        margin: 0.5in;
                    }

                    html, body {
                        margin: 0 !important;
                        padding: 0 !important;
                        width: 100% !important;
                        height: auto !important;
                        overflow: visible !important;
                        print-color-adjust: exact;
                        -webkit-print-color-adjust: exact;
                    }

                    /* Remove all scrolling and height constraints from all elements */
                    * {
                        overflow: visible !important;
                        max-height: none !important;
                    }

                    /* Critical: Override MainLayout constraints that cause scroll-based printing */
                    /* MainLayout has h-screen and overflow-auto that must be removed */
                    .flex.h-screen,
                    main.overflow-auto,
                    main {
                        height: auto !important;
                        min-height: 0 !important;
                        max-height: none !important;
                        overflow: visible !important;
                    }

                    /* Force all flex containers to allow natural height */
                    .flex,
                    .flex-1,
                    .flex-col {
                        height: auto !important;
                        min-height: 0 !important;
                    }

                    /* Target React root and all parent divs */
                    #root,
                    #root > div,
                    body > div {
                        height: auto !important;
                        min-height: 0 !important;
                        overflow: visible !important;
                    }

                    /* Each print page */
                    .print-page {
                        display: block;
                        position: relative;
                        width: 100%;
                        margin: 0;
                        padding: 0;
                        page-break-after: always;
                    }

                    .print-page:last-child {
                        page-break-after: auto;
                    }

                    /* Keep customer info and shipping sections together */
                    .customer-info-box,
                    .shipping-info-section {
                        page-break-inside: avoid;
                    }

                    .page-header {
                        display: block;
                        width: 100%;
                        margin-bottom: 1rem;
                    }

                    .page-footer {
                        display: block;
                        width: 100%;
                        margin-top: 1rem;
                    }

                    /* Hide screen-only elements */
                    .screen-header,
                    .screen-footer,
                    .screen-only {
                        display: none !important;
                    }

                    /* Show print-only elements */
                    .print-only {
                        display: block !important;
                        margin: 0 !important;
                        padding: 0 !important;
                    }

                    /* Ensure first print page starts at top */
                    .print-only > .print-page:first-child {
                        margin-top: 0 !important;
                        padding-top: 0 !important;
                    }

                    /* Container resets */
                    .container {
                        width: 100% !important;
                        max-width: 100% !important;
                        margin: 0 !important;
                        padding: 0 !important;
                        height: auto !important;
                        overflow: visible !important;
                    }

                    .packing-slip-container {
                        box-shadow: none !important;
                        border-radius: 0 !important;
                        padding: 0 !important;
                        margin: 0 !important;
                        height: auto !important;
                        overflow: visible !important;
                    }

                    .packing-slip-content {
                        width: 100% !important;
                        padding: 0 !important;
                        margin: 0 !important;
                        height: auto !important;
                        overflow: visible !important;
                    }

                    /* Product table styles */
                    .packing-slip-content table {
                        width: 100%;
                        border-collapse: collapse;
                        page-break-inside: auto;
                        margin: 0;
                    }

                    .packing-slip-content table,
                    .packing-slip-content th,
                    .packing-slip-content td {
                        border: 1px solid #d1d5db;
                    }
                    
                    .packing-slip-content td.border-none {
                        border: none !important;
                    }

                    .packing-slip-content th,
                    .packing-slip-content td {
                        padding: 8px;
                        text-align: left;
                    }

                    .packing-slip-content table tr {
                       
                    }

                    /* Allow page breaks in the product table tbody */
                    .packing-slip-content table tbody {
                        
                    }

                    .packing-slip-content tbody tr:empty {
                        
                    }

                    /* Hide empty spacer rows in print */
                    .packing-slip-content tr.empty-row {
                       
                    }

                    .packing-slip-content thead {
                        display: table-header-group;
                    }

                    /* Other element styles */
                    .bg-gray-100 {
                        background-color: #fff !important;
                        padding: 1rem;
                    }

                    .border-b {
                        border-bottom: 1px solid #9ca3af;
                    }

                    .border-t {
                        border-top: 1px solid #e5e7eb;
                    }

                    .page-break {
                        page-break-after: always;
                    }

                    .mb-8 {
                        margin-bottom: 1.5rem;
                    }

                    .mb-4 {
                        margin-bottom: 0.75rem;
                    }

                    .grid {
                        display: grid;
                        page-break-inside: auto;
                    }

                    .grid-cols-2 {
                        grid-template-columns: repeat(2, minmax(0, 1fr));
                    }

                    .gap-8 {
                        gap: 2rem;
                    }

                    /* Reduce section margins to allow more content per page */
                    .mb-8 {
                        margin-bottom: 1rem !important;
                    }

                    .mb-4 {
                        margin-bottom: 0.5rem !important;
                    }
                }
            `}</style>

            <div className="container mx-auto px-4 py-6">
                {/* Screen-only header with actions */}
                <div className="no-print mb-6 flex justify-between items-center">
                    <button
                        onClick={() => navigate('/packing-slips')}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                    >
                        ← Back to List
                    </button>
                    <button
                        onClick={handlePrint}
                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 flex items-center"
                    >
                        <svg className="h-5 w-5 mr-2" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M5 4v3H4a2 2 0 00-2 2v3a2 2 0 002 2h1v2a2 2 0 002 2h6a2 2 0 002-2v-2h1a2 2 0 002-2V9a2 2 0 00-2-2h-1V4a2 2 0 00-2-2H7a2 2 0 00-2 2zm8 0H7v3h6V4zm0 8H7v4h6v-4z" clipRule="evenodd" />
                        </svg>
                        Print Packing Slip
                    </button>
                </div>

                {/* Printable packing slip */}
                <div className="packing-slip-container bg-white rounded-lg shadow-lg p-8 max-w-4xl mx-auto">
                    <div className="packing-slip-content">
                        {/* Logo / Company Header - screen only */}
                        <div className="screen-header relative mb-8 flex items-center">
                            <div className="flex-shrink-0">
                                <img src={logoSrc} alt="Fit2Ride Logo" className="h-20 w-20" />
                            </div>
                            <div className="absolute left-0 right-0 text-center pointer-events-none">
                                <div className="text-2xl font-bold text-gray-800 mb-1">FIT PROTECTION</div>
                                <div className="text-sm text-gray-600">PACKING SLIP</div>
                            </div>
                        </div>

                        {/* Company & Customer Info */}
                        <div className="mb-8 screen-only">
                            <div className="bg-gray-100 p-4 mb-4">
                                <h3 className="font-bold text-gray-800 mb-2">Customer Name:</h3>
                                <div className="space-y-1">
                                    <div>Company: VIDA Distribution (3PL)</div>
                                    <div>9245 S Farmer Ave STE. 110, Tempe, AZ 85284</div>
                                    <div>Phone: (480) 247-2337</div>
                                    <div>Email: receiving@vidadistro.com</div>
                                </div>
                            </div>
                        </div>

                        {/* Products Table - Screen version (all items in one table) */}
                        <div className="screen-only">
                            <div className="mb-8">
                                <table className="min-w-full border-collapse border border-gray-300">
                                    <thead>
                                        <tr className="bg-gray-100">
                                            <th className="border border-gray-300 px-4 py-2 text-left font-semibold">Product Description</th>
                                            <th className="border border-gray-300 px-4 py-2 text-left font-semibold">SKU</th>
                                            <th className="border border-gray-300 px-4 py-2 text-center font-semibold">Quantity</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {packingSlip.items && packingSlip.items.map((item, index) => (
                                            <tr key={index}>
                                                <td className="border border-gray-300 px-4 py-3">{item.product_description}</td>
                                                <td className="border border-gray-300 px-4 py-3">{item.product_sku}</td>
                                                <td className="border border-gray-300 px-4 py-3 text-center">{item.quantity}</td>
                                            </tr>
                                        ))}
                                        {/* Add empty rows for spacing if needed */}
                                        {packingSlip.items && Array.from({ length: Math.max(0, 6 - packingSlip.items.length) }).map((_, index) => (
                                            <tr key={`empty-${index}`}>
                                                <td className="border border-gray-300 px-4 py-3">&nbsp;</td>
                                                <td className="border border-gray-300 px-4 py-3">&nbsp;</td>
                                                <td className="border border-gray-300 px-4 py-3">&nbsp;</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {/* Shipping & Receiving Info */}
                            <div className="grid grid-cols-2 gap-8 mb-8">
                                <div>
                                    <div className="mb-4">
                                        <div className="font-semibold mb-2">Date Packed:</div>
                                        <div className="border-b border-gray-400 pb-1">
                                            {packingSlip.date_packed ? format(new Date(packingSlip.date_packed), 'MM/dd/yyyy') : '_______________'}
                                        </div>
                                    </div>
                                    <div className="mb-4">
                                        <div className="font-semibold mb-2">Date Shipped:</div>
                                        <div className="border-b border-gray-400 pb-1">
                                            {packingSlip.date_shipped ? format(new Date(packingSlip.date_shipped), 'MM/dd/yyyy') : '_______________'}
                                        </div>
                                    </div>
                                    <div>
                                        <div className="font-semibold mb-2">Packed By:</div>
                                        <div className="border-b border-gray-400 pb-1">
                                            {packingSlip.packed_by || '_______________'}
                                        </div>
                                    </div>
                                </div>
                                <div>
                                    <div className="mb-4">
                                        <div className="font-semibold mb-2">Date Received:</div>
                                        <div className="border-b border-gray-400 pb-1">
                                            {packingSlip.date_received ? format(new Date(packingSlip.date_received), 'MM/dd/yyyy') : '_______________'}
                                        </div>
                                    </div>
                                    <div>
                                        <div className="font-semibold mb-2">Received By:</div>
                                        <div className="border-b border-gray-400 pb-1">
                                            {packingSlip.received_by || '_______________'}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Special Instructions */}
                            <div className="mb-8">
                                <div className="font-semibold mb-2">Special Instructions / Comments</div>
                                <div className="border-b border-gray-400 pb-1 min-h-[60px]">
                                    {packingSlip.special_instructions || ''}
                                </div>
                            </div>
                        </div>

                        {/* Products Table - Print version (paginated with headers/footers per page) */}
                        <div className="print-only">
                            <PrintablePackingSlip packingSlip={packingSlip} />
                        </div>

                        {/* Footer with Batch Numbers and Company Address - screen only */}
                        <div className="screen-footer text-sm text-gray-600 border-t pt-4 flex justify-between">
                            <div>
                                <div>Packing Slip #: {packingSlip.packing_slip_number}</div>
                                <div>Production Batch #: {packingSlip.production_batch?.batch_number}</div>
                                <div>Created: {format(new Date(packingSlip.created_at), 'MM/dd/yyyy')}</div>
                            </div>
                            <div className="text-right">
                                <div className="font-semibold">FIT PROTECTION</div>
                                <div>3215 E WASHINGTON ST</div>
                                <div>PHOENIX, AZ 85395</div>
                                <div>INFO@FITPROTECTION.COM</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default PackingSlipDetail;
